import posthog from 'posthog-js';

const apiKey = import.meta.env.VITE_POSTHOG_KEY ?? '';
const uiHost = import.meta.env.VITE_POSTHOG_UI_HOST ?? 'https://us.posthog.com';

/** Produção: sempre proxy same-origin (/adl-e). Dev: VITE_POSTHOG_HOST ou ingestão direta. */
function resolvePostHogApiHost(): string {
  if (import.meta.env.PROD && typeof window !== 'undefined') {
    return `${window.location.origin}/adl-e`;
  }
  return import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com';
}

let initialized = false;

function safePostHog<T>(run: (client: typeof posthog) => T): T | undefined {
  try {
    const client = getPostHog();
    if (!client) return undefined;
    return run(client);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[PostHog] analytics call failed:', error);
    }
    return undefined;
  }
}

export function isPostHogEnabled(): boolean {
    // Evita "request timed out" e spam de logs durante desenvolvimento local
    // quando o ambiente não tem conectividade garantida.
    const devAllow =
        import.meta.env.VITE_ENABLE_POSTHOG === 'true' || import.meta.env.VITE_ENABLE_POSTHOG === '1';
    if (import.meta.env.DEV && !devAllow) return false;

    return Boolean(apiKey);
}

export function initPostHog(): typeof posthog | null {
  if (initialized || !isPostHogEnabled()) return initialized ? posthog : null;

  try {
    posthog.init(apiKey, {
      api_host: resolvePostHogApiHost(),
      ui_host: uiHost,
      person_profiles: 'always',
      capture_pageview: false,
      capture_pageleave: true,
      // Evita carregar "site apps" de terceiros (superfície extra + incompatível com CSP estrita).
      opt_in_site_apps: false,
      session_recording: {
        maskAllInputs: true,
      },
      autocapture: {
        dom_event_allowlist: ['click', 'submit'],
        css_selector_ignorelist: [
          '.ph-no-capture',
          '.ph-no-autocapture',
          '[data-ph-no-autocapture]',
          '[data-ga-exclude="true"]',
          'input[type="password"]',
          'input[type="email"]',
          'input[type="tel"]',
        ],
      },
      capture_performance: { web_vitals: true },
      persistence: 'localStorage+cookie',
      loaded: (client) => {
        if (import.meta.env.DEV) {
          client.debug(true);
        }
      },
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[PostHog] init failed:', error);
    }
    return null;
  }

  initialized = true;
  return posthog;
}

export function getPostHog(): typeof posthog | null {
  if (!initialized) return initPostHog();
  return isPostHogEnabled() ? posthog : null;
}

export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>,
): void {
  safePostHog((client) => client.identify(userId, properties));
}

export function resetUser(): void {
  safePostHog((client) => client.reset());
}

export function identifyOrganization(
  organizationId: string,
  properties?: Record<string, unknown>,
): void {
  safePostHog((client) => client.group('organization', organizationId, properties));
}

export function capturePageview(page: string, path: string): void {
  safePostHog((client) => client.capture('$pageview', {
    page,
    $current_url: `${window.location.origin}${path}`,
    $pathname: path,
  }));
}

export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  safePostHog((client) => client.capture(event, properties));
}

export function captureException(error: Error, properties?: Record<string, unknown>): void {
  safePostHog((client) => {
    if (typeof client.captureException === 'function') {
      client.captureException(error, properties);
      return;
    }
    client.capture('$exception', {
      ...properties,
      $exception_message: error.message,
      $exception_type: error.name,
    });
  });
}
